import { confirmDialog, showDialog } from '@/utils/dialogs';
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Switch, TextInput, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { apiRequest } from '../../services/apiClient';
import * as Sentry from '@sentry/react-native';
import {
  AdminCalloutBanner,
  AdminSectionCard,
  AdminStatusPill,
  ADMIN_COLORS,
} from '@/components/admin/AdminUI';
import EvidenceFeedbackStatsPanel from '@/components/admin/EvidenceFeedbackStatsPanel';

type ReasoningEffort = 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';

// Live model routing is owned by Workflow Model Routing (the /admin/model-assignments
// store). The runtime resolves those assignments FIRST and only falls back to the
// GradingConfiguration fields below when a workload has no assignment — so the audit
// panel reads assignments directly to show what is actually active.
interface WorkloadAssignment {
  workload_key: string;
  model: string;
  max_tokens: number;
  reasoning_effort: ReasoningEffort | null;
}

interface WorkloadAssignmentsResponse {
  assignments: Record<string, WorkloadAssignment>;
  workload_keys: string[];
}

export interface GradingConfiguration {
  include_cctr: boolean;
  include_q_types: boolean;
  include_what_who_framework: boolean;
  include_veto_rules: boolean;
  include_enforcement_rules: boolean;
  use_evidence_first_template: boolean;
  grading_harshness: 'supportive' | 'balanced' | 'strict';
  phase3_output_mode: 'json' | 'markdown';
  phase3_provider: 'openrouter' | null;
  phase3_model: string | null;
  phase3_reasoning_effort: ReasoningEffort;
  phase3_response_tokens_limit: number;
  phase2_contract: 'competency_v1';
  phase2_mode: 'workers' | 'single_evaluator' | 'hybrid';
  phase2_model: string | null;
  phase2_reasoning_effort: ReasoningEffort;
  phase2_request_limit: number;
  phase2_response_tokens_limit: number;
  phase2_worker_parallelism: number;
  phase2_target_evidence_per_competency: number;
  phase2_min_evidence_per_competency: number;
  phase2_fail_on_missing_competency: boolean;
  phase2_calibration_profile: 'legacy' | 'balanced_v2';
  // WS-1 statement-level evidence knobs (Tier 3 mentor SOF/CRF instrument pass).
  instrument_evidence_max_per_statement: number;
  instrument_capture_counter_evidence: boolean;
  // Evidence feedback loop (B1): mentor relevance judgements feed calibration notes back into the instrument prompt.
  evidence_feedback_calibration_enabled: boolean;
  evidence_feedback_min_judgements: number;
  evidence_feedback_max_notes: number;
}

interface GradingConfigurationPanelProps {
  onConfigChange?: (config: GradingConfiguration) => void;
}

const DEFAULT_CONFIG: GradingConfiguration = {
  include_cctr: false,
  include_q_types: false,
  include_what_who_framework: true,
  include_veto_rules: true,
  include_enforcement_rules: true,
  use_evidence_first_template: true,
  grading_harshness: 'balanced',
  phase3_output_mode: 'markdown',
  phase3_provider: 'openrouter',
  phase3_model: 'anthropic/claude-haiku-4.5',
  phase3_reasoning_effort: 'low',
  phase3_response_tokens_limit: 12000,
  phase2_contract: 'competency_v1',
  phase2_mode: 'single_evaluator',
  phase2_model: null,
  phase2_reasoning_effort: 'high',
  phase2_request_limit: 4,
  phase2_response_tokens_limit: 8192,
  phase2_worker_parallelism: 4,
  phase2_target_evidence_per_competency: 10,
  phase2_min_evidence_per_competency: 4,
  phase2_fail_on_missing_competency: true,
  phase2_calibration_profile: 'legacy',
  instrument_evidence_max_per_statement: 8,
  instrument_capture_counter_evidence: true,
  evidence_feedback_calibration_enabled: true,
  evidence_feedback_min_judgements: 3,
  evidence_feedback_max_notes: 12,
};

const normalizeConfig = (response: Partial<GradingConfiguration>): GradingConfiguration => ({
  ...DEFAULT_CONFIG,
  ...response,
  phase3_provider: 'openrouter',
  phase2_contract: 'competency_v1',
});

const showMessage = (title: string, message: string) => {
  showDialog(title, message);
};

interface ToggleRowProps {
  title: string;
  body: string;
  value: boolean;
  onChange: (value: boolean) => void;
  status?: 'Live' | 'Capability gated' | 'Advanced';
}

function ToggleRow({ title, body, value, onChange, status = 'Live' }: ToggleRowProps) {
  const statusTone = status === 'Live' ? 'success' : status === 'Capability gated' ? 'warning' : 'primary';

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 14,
        backgroundColor: ADMIN_COLORS.surface,
        padding: 14,
        gap: 10,
      }}
    >
      <View className="flex-row items-start justify-between" style={{ gap: 14 }}>
        <View style={{ flex: 1, gap: 6 }}>
          <View className="flex-row items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
              {title}
            </Text>
            <AdminStatusPill label={status} tone={statusTone} />
          </View>
          <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
            {body}
          </Text>
        </View>
        <Switch
          value={value}
          onValueChange={onChange}
          thumbColor={value ? '#FFFFFF' : '#F4F4F5'}
          trackColor={{ false: ADMIN_COLORS.border, true: ADMIN_COLORS.primary }}
        />
      </View>
    </View>
  );
}

interface NumberRowProps {
  title: string;
  body: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  status?: 'Live' | 'Capability gated' | 'Advanced';
}

function NumberRow({ title, body, value, min, max, onChange, status = 'Live' }: NumberRowProps) {
  const [text, setText] = useState(String(value));

  useEffect(() => {
    setText(String(value));
  }, [value]);

  const commit = (next: string) => {
    setText(next);
    const parsed = Number(next);
    if (!Number.isFinite(parsed)) return;
    onChange(Math.min(max, Math.max(min, Math.round(parsed))));
  };

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: ADMIN_COLORS.border,
        borderRadius: 14,
        backgroundColor: ADMIN_COLORS.surface,
        padding: 14,
        gap: 10,
      }}
    >
      <View className="flex-row items-start justify-between" style={{ gap: 14 }}>
        <View style={{ flex: 1, gap: 6 }}>
          <View className="flex-row items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
              {title}
            </Text>
            <AdminStatusPill label={status} tone={status === 'Live' ? 'success' : 'primary'} />
          </View>
          <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
            {body}
          </Text>
        </View>
        <TextInput
          value={text}
          onChangeText={commit}
          onBlur={() => setText(String(value))}
          keyboardType="numeric"
          style={{
            width: 88,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.border,
            borderRadius: 10,
            paddingHorizontal: 10,
            paddingVertical: 8,
            color: ADMIN_COLORS.text.primary,
            textAlign: 'center',
            backgroundColor: ADMIN_COLORS.background,
          }}
        />
      </View>
      <Text className="text-xs" style={{ color: ADMIN_COLORS.text.muted }}>
        {`Allowed range: ${min}–${max}`}
      </Text>
    </View>
  );
}

interface ChoiceRowProps<TValue extends string> {
  value: TValue;
  selected: boolean;
  title: string;
  body: string;
  onPress: (value: TValue) => void;
  badge?: string;
}

function ChoiceRow<TValue extends string>({
  value,
  selected,
  title,
  body,
  onPress,
  badge,
}: ChoiceRowProps<TValue>) {
  return (
    <TouchableOpacity
      onPress={() => onPress(value)}
      activeOpacity={0.86}
      style={{
        borderWidth: 1,
        borderColor: selected ? ADMIN_COLORS.primary : ADMIN_COLORS.border,
        borderRadius: 14,
        backgroundColor: selected ? ADMIN_COLORS.primaryFaint : ADMIN_COLORS.background,
        padding: 14,
      }}
    >
      <View className="flex-row items-start" style={{ gap: 12 }}>
        <MaterialIcons
          name={selected ? 'radio-button-checked' : 'radio-button-unchecked'}
          size={20}
          color={selected ? ADMIN_COLORS.primary : ADMIN_COLORS.text.muted}
        />
        <View style={{ flex: 1, gap: 4 }}>
          <View className="flex-row items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
              {title}
            </Text>
            {badge ? <AdminStatusPill label={badge} tone="primary" /> : null}
          </View>
          <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
            {body}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <View
      style={{
        minWidth: 170,
        flex: 1,
        borderWidth: 1,
        borderColor: ADMIN_COLORS.borderSoft,
        borderRadius: 12,
        backgroundColor: ADMIN_COLORS.surface,
        padding: 12,
      }}
    >
      <Text
        className="text-xs font-semibold uppercase"
        style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.3 }}
      >
        {label}
      </Text>
      <Text className="text-sm font-bold mt-1" style={{ color: ADMIN_COLORS.text.primary }}>
        {String(value)}
      </Text>
    </View>
  );
}

export default function GradingConfigurationPanel({ onConfigChange }: GradingConfigurationPanelProps) {
  const [config, setConfig] = useState<GradingConfiguration>(DEFAULT_CONFIG);
  const [assignments, setAssignments] = useState<Record<string, WorkloadAssignment>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalConfig, setOriginalConfig] = useState<GradingConfiguration | null>(null);

  const hasChanges = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(originalConfig),
    [config, originalConfig],
  );

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const [response, assignmentsResponse] = await Promise.all([
        apiRequest<GradingConfiguration>({
          endpoint: '/admin/grading-configuration',
          method: 'GET',
        }),
        apiRequest<WorkloadAssignmentsResponse>({
          endpoint: '/admin/model-assignments',
          method: 'GET',
        }).catch((error) => {
          // Audit display is best-effort — a routing fetch failure must not block controls.
          console.error('Failed to fetch model assignments:', error);
          return { assignments: {}, workload_keys: [] } as WorkloadAssignmentsResponse;
        }),
      ]);
      const fetchedConfig = normalizeConfig(response);
      setConfig(fetchedConfig);
      setOriginalConfig(fetchedConfig);
      setAssignments(assignmentsResponse.assignments || {});
    } catch (error) {
      console.error('Failed to fetch grading configuration:', error);
      Sentry.captureException(error);
      showMessage('Error', 'Failed to load grading configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const updateConfig = (updates: Partial<GradingConfiguration>) => {
    setConfig((current) => normalizeConfig({ ...current, ...updates }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...config,
        phase3_provider: 'openrouter' as const,
        phase3_model: config.phase3_model?.trim() || null,
        phase2_model: config.phase2_model?.trim() || null,
      };
      const response = await apiRequest<GradingConfiguration, GradingConfiguration>({
        endpoint: '/admin/grading-configuration',
        method: 'PUT',
        data: payload,
      });
      const updatedConfig = normalizeConfig(response);
      setConfig(updatedConfig);
      setOriginalConfig(updatedConfig);
      onConfigChange?.(updatedConfig);
      showMessage('Success', 'Grading configuration updated.');
    } catch (error) {
      console.error('Failed to save grading configuration:', error);
      Sentry.captureException(error);
      showMessage('Error', 'Failed to save grading configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleReseedFrameworks = async () => {
    try {
      setSaving(true);
      const response = await apiRequest<{ seeded: string[] }>({
        endpoint: '/admin/frameworks/reseed',
        method: 'POST',
      });
      showMessage('Success', `ICF frameworks re-seeded (${response.seeded.length}). Marker text now matches the repo YAML.`);
    } catch (error) {
      console.error('Failed to re-seed ICF frameworks:', error);
      Sentry.captureException(error);
      showMessage('Error', 'Failed to re-seed ICF frameworks.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const runReset = async () => {
      try {
        setSaving(true);
        const response = await apiRequest<GradingConfiguration>({
          endpoint: '/admin/grading-configuration/reset',
          method: 'POST',
        });
        const resetConfig = normalizeConfig(response);
        setConfig(resetConfig);
        setOriginalConfig(resetConfig);
        onConfigChange?.(resetConfig);
        showMessage('Success', 'Configuration reset to defaults.');
      } catch (error) {
        console.error('Failed to reset grading configuration:', error);
        Sentry.captureException(error);
        showMessage('Error', 'Failed to reset grading configuration.');
      } finally {
        setSaving(false);
      }
    };

    const confirmed = await confirmDialog(
      'Reset to Defaults',
      'Reset the global grading configuration to defaults?',
      'Reset',
      'Cancel',
    );
    if (confirmed) await runReset();
  };

  const handleCancel = () => {
    if (originalConfig) {
      setConfig(originalConfig);
    }
  };

  // Read live routing values from the Workflow Model Routing assignments (what the
  // runtime resolves first), not the legacy GradingConfiguration fallback fields.
  const assignmentField = (
    key: string,
    field: 'model' | 'reasoning_effort' | 'max_tokens',
  ): string | number => {
    const assignment = assignments[key];
    if (!assignment) return 'Workflow default';
    const value = assignment[field];
    return value === null || value === undefined ? '—' : value;
  };

  if (loading) {
    return (
      <AdminSectionCard icon="tune" title="AI Grading Configuration" subtitle="Loading live runtime settings.">
        <View className="items-center justify-center py-8" style={{ gap: 10 }}>
          <ActivityIndicator size="large" color={ADMIN_COLORS.primary} />
          <Text className="text-sm" style={{ color: ADMIN_COLORS.text.secondary }}>
            Loading grading controls...
          </Text>
        </View>
      </AdminSectionCard>
    );
  }

  return (
    <AdminSectionCard
      icon="tune"
      title="AI Grading Configuration"
      subtitle="Only live runtime controls are shown here. Model choices and token budgets are handled in Workflow Model Routing."
      headerRight={hasChanges ? <AdminStatusPill label="Unsaved" tone="warning" /> : <AdminStatusPill label="Live" tone="success" />}
    >
      <AdminCalloutBanner
        tone="info"
        body="These settings apply globally to future analyses. School AI policies can still gate some features by plan capability, so a global switch may be live but unavailable for lower tiers."
      />

      <View style={{ gap: 14 }}>
        <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
          Core Analysis Controls
        </Text>
        <View style={{ gap: 10 }}>
          {([
            ['balanced', 'Balanced', 'Default professional standard with equal weight on strengths and development.'],
            ['supportive', 'Supportive', 'More encouraging feedback tone while keeping evidence requirements intact.'],
            ['strict', 'Strict', 'Higher directness for gap identification and ICF compliance.'],
          ] as const).map(([value, title, body]) => (
            <ChoiceRow
              key={value}
              value={value}
              selected={config.grading_harshness === value}
              title={title}
              body={body}
              badge={value === 'balanced' ? 'Default' : undefined}
              onPress={(next) => updateConfig({ grading_harshness: next })}
            />
          ))}
        </View>

        <View style={{ gap: 10 }}>
          {([
            ['single_evaluator', 'Single Strong Evaluator', 'One evaluator scores the full coach profile. Current production default.'],
            ['hybrid', 'Hybrid + Adjudicator', 'Evaluator plus senior adjudicator validation. Better quality, higher cost.'],
            ['workers', 'Parallel Workers', 'C1-C8 work split across workers. Kept for fallback and comparative testing.'],
          ] as const).map(([value, title, body]) => (
            <ChoiceRow
              key={value}
              value={value}
              selected={config.phase2_mode === value}
              title={title}
              body={body}
              onPress={(next) => updateConfig({ phase2_mode: next })}
            />
          ))}
        </View>

        <View style={{ gap: 10 }}>
          {([
            ['markdown', 'Markdown Output', 'Recommended output contract. More reliable across current OpenRouter models.'],
            ['json', 'JSON Output', 'Legacy strict schema path. Keep only for rollback or controlled testing.'],
          ] as const).map(([value, title, body]) => (
            <ChoiceRow
              key={value}
              value={value}
              selected={config.phase3_output_mode === value}
              title={title}
              body={body}
              badge={value === 'markdown' ? 'Recommended' : 'Legacy'}
              onPress={(next) => updateConfig({ phase3_output_mode: next })}
            />
          ))}
        </View>

        <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
          Level Calibration Profile
        </Text>
        <View style={{ gap: 10 }}>
          {([
            ['legacy', 'Legacy', 'Current downgrade-only calibration. MCC is unreachable in the default flow (requires MCC BARS ratings that only run on explicit MCC requests).'],
            ['balanced_v2', 'Balanced v2 (MCC auto-escalation)', 'When a session’s competency profile supports MCC and only the MCC BARS confirmation is missing, runs a one-call MCC BARS pass so genuine MCC sessions can reach MCC. Can only raise a level, never lower it.'],
          ] as const).map(([value, title, body]) => (
            <ChoiceRow
              key={value}
              value={value}
              selected={config.phase2_calibration_profile === value}
              title={title}
              body={body}
              badge={value === 'legacy' ? 'Default' : 'Testing'}
              onPress={(next) => updateConfig({ phase2_calibration_profile: next })}
            />
          ))}
        </View>
      </View>

      <View style={{ gap: 12 }}>
        <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
          Optional Scoring Signals
        </Text>
        <ToggleRow
          title="CCTR Metric"
          body="Adds client/coach talk-time ratio guidance to the narrative agent."
          status="Live"
          value={config.include_cctr}
          onChange={(value) => updateConfig({ include_cctr: value })}
        />
        <ToggleRow
          title="Question Type Taxonomy"
          body="Adds Q1/Q2/Q3 question classification guidance."
          status="Live"
          value={config.include_q_types}
          onChange={(value) => updateConfig({ include_q_types: value })}
        />
        <ToggleRow
          title="What/Who Framework"
          body="Adds What/Who balance guidance. School policy capabilities can still disable it for some tiers."
          status="Capability gated"
          value={config.include_what_who_framework}
          onChange={(value) => updateConfig({ include_what_who_framework: value })}
        />
      </View>

      <View style={{ gap: 12 }}>
        <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
          Statement Evidence (Tier 3 Observation Forms)
        </Text>
        <NumberRow
          title="Max evidence citations per statement"
          body="Upper bound on supporting citations the instrument agents may return for each of the 78 official form statements."
          value={config.instrument_evidence_max_per_statement}
          min={2}
          max={20}
          status="Advanced"
          onChange={(value) => updateConfig({ instrument_evidence_max_per_statement: value })}
        />
        <ToggleRow
          title="Capture counter-evidence"
          body="Also collect contradicting moments for each statement. Needed for audit-grade Session Observation Forms."
          status="Advanced"
          value={config.instrument_capture_counter_evidence}
          onChange={(value) => updateConfig({ instrument_capture_counter_evidence: value })}
        />
      </View>

      <View style={{ gap: 12 }} testID="evidence_feedback_card">
        <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
          Evidence Feedback Loop (mentor relevance judgements)
        </Text>
        <ToggleRow
          title="Calibration from mentor feedback"
          body="Turn mentors' 'not relevant' marks on SOF citations into short calibration notes for the instrument prompt. Off keeps judgements stored but unused."
          status="Advanced"
          value={config.evidence_feedback_calibration_enabled}
          onChange={(value) => updateConfig({ evidence_feedback_calibration_enabled: value })}
        />
        <NumberRow
          title="Minimum judgements per statement"
          body="A statement only gets a calibration note once this many citations have been judged by mentors."
          value={config.evidence_feedback_min_judgements}
          min={3}
          max={50}
          status="Advanced"
          onChange={(value) => updateConfig({ evidence_feedback_min_judgements: value })}
        />
        <NumberRow
          title="Maximum calibration notes"
          body="Upper bound on notes injected per run, so the prompt stays short even when many statements have feedback. 0 disables injection."
          value={config.evidence_feedback_max_notes}
          min={0}
          max={12}
          status="Advanced"
          onChange={(value) => updateConfig({ evidence_feedback_max_notes: value })}
        />
        <EvidenceFeedbackStatsPanel />
      </View>

      <View style={{ gap: 12 }}>
        <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
          Guardrails
        </Text>
        <ToggleRow
          title="Veto Rules"
          body="Keeps ethical, structural, and language violations from receiving inflated scores."
          status="Capability gated"
          value={config.include_veto_rules}
          onChange={(value) => updateConfig({ include_veto_rules: value })}
        />
        <ToggleRow
          title="AI Grounding Rules"
          body="Keeps instruction-following and evidence-grounding rules in the grading prompt."
          status="Live"
          value={config.include_enforcement_rules}
          onChange={(value) => updateConfig({ include_enforcement_rules: value })}
        />
        <ToggleRow
          title="Evidence-First Template"
          body="Forces evidence before conclusion in the narrative structure."
          status="Live"
          value={config.use_evidence_first_template}
          onChange={(value) => updateConfig({ use_evidence_first_template: value })}
        />
        <ToggleRow
          title="Fail on Missing Competency"
          body="Fails closed when competency scoring is incomplete after repair."
          status="Advanced"
          value={config.phase2_fail_on_missing_competency}
          onChange={(value) => updateConfig({ phase2_fail_on_missing_competency: value })}
        />
      </View>

      <View style={{ gap: 12 }}>
        <Text className="text-sm font-bold" style={{ color: ADMIN_COLORS.text.primary }}>
          Runtime Details
        </Text>
        <View className="flex-row flex-wrap" style={{ gap: 10 }}>
          <DetailRow label="Scoring Contract" value={config.phase2_contract} />
          <DetailRow label="Phase 2 Mode" value={config.phase2_mode} />
          <DetailRow label="Phase 2 Model" value={assignmentField('analysis.phase2_evaluate', 'model')} />
          <DetailRow label="Phase 2 Reasoning" value={assignmentField('analysis.phase2_evaluate', 'reasoning_effort')} />
          <DetailRow label="Phase 2 Tokens" value={assignmentField('analysis.phase2_evaluate', 'max_tokens')} />
          {config.phase2_mode === 'hybrid' ? (
            <>
              <DetailRow label="Adjudicator Model" value={assignmentField('analysis.phase2_adjudicate', 'model')} />
              <DetailRow label="Adjudicator Reasoning" value={assignmentField('analysis.phase2_adjudicate', 'reasoning_effort')} />
              <DetailRow label="Adjudicator Tokens" value={assignmentField('analysis.phase2_adjudicate', 'max_tokens')} />
            </>
          ) : null}
          {config.phase2_mode === 'workers' ? (
            <DetailRow label="Worker Parallelism" value={config.phase2_worker_parallelism} />
          ) : null}
          <DetailRow label="Phase 3 Model" value={assignmentField('analysis.phase3_narrative', 'model')} />
          <DetailRow label="Phase 3 Reasoning" value={assignmentField('analysis.phase3_narrative', 'reasoning_effort')} />
          <DetailRow label="Phase 3 Tokens" value={assignmentField('analysis.phase3_narrative', 'max_tokens')} />
          <DetailRow
            label="Evidence Target"
            value={`${config.phase2_target_evidence_per_competency} target / ${config.phase2_min_evidence_per_competency} min`}
          />
        </View>
        <AdminCalloutBanner
          tone="info"
          body="Live model routing — these reflect the active Workflow Model Routing assignments the runtime actually uses. To change a model, reasoning effort, or token budget, edit the matching workload there; this panel updates from the same source."
        />
      </View>

      <View className="flex-row flex-wrap" style={{ gap: 10 }}>
        <Button
          onPress={handleSave}
          disabled={!hasChanges || saving}
          className="flex-row items-center justify-center gap-2"
          style={{ flexGrow: 1 }}
        >
          {saving ? <ActivityIndicator size="small" color="#FFFFFF" /> : <MaterialIcons name="save" size={18} color="#FFFFFF" />}
          <Text className="text-sm font-semibold text-primary-foreground">
            {saving ? 'Saving...' : 'Save Changes'}
          </Text>
        </Button>

        {hasChanges ? (
          <Button onPress={handleCancel} disabled={saving} variant="outline" style={{ flexGrow: 1 }}>
            <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
              Cancel
            </Text>
          </Button>
        ) : (
          <Button onPress={handleReset} disabled={saving} variant="outline" style={{ flexGrow: 1 }}>
            <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.errorText }}>
              Reset Defaults
            </Text>
          </Button>
        )}
      </View>
      <View style={{ marginTop: 12 }}>
        <Button onPress={handleReseedFrameworks} disabled={saving} variant="outline" style={{ flexGrow: 1 }}>
          <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
            Re-seed ICF frameworks from YAML (MSR rev. 1.26.2026)
          </Text>
        </Button>
      </View>
    </AdminSectionCard>
  );
}
